import { isClosedStatus, type UserTask } from "@/types/userTasks.ts";

/**
 * The groups the list shows, in order, by status and then by who holds the
 * task.
 *
 * Ordered by what the user owes, not by where a task is in its life. Needs you
 * leads because it is the only group that is blocked on them, and it carries
 * okthx's work too once that work is waiting on them. The two in-flight groups
 * follow: something is happening and they may want to see it, but neither is
 * asking. Suggested sits below both — okthx offering a task is the least
 * pressing thing on the page, however new it is. Later and Closed trail as the
 * record.
 */
export const GROUPS: { key: string; label: string; has: (task: UserTask) => boolean }[] = [
  {
    key: "needsYou",
    // The same words the filter and the indicator use. It read "Needs you"
    // here, so one group answered to two names depending on whether the reader
    // met it as a heading or as a chip.
    label: "Needs your input",
    has: (t) =>
      t.status === "open" &&
      ((t.assignee === "user" && !t.waitingOn && !t.heldAtMs) ||
        (t.assignee === "okthx" && !!t.needsInput)),
  },
  {
    key: "okthx",
    label: "Okthx is working on",
    has: (t) => t.status === "open" && t.assignee === "okthx" && !t.needsInput,
  },
  {
    key: "waiting",
    // "someone" rather than "someone else", which asked the reader to work out
    // who it was being contrasted with. It also matches what a task says about
    // itself when the person has no name on them — see `taskHolder`.
    label: "Waiting on someone",
    // The user's own: a task okthx owns stays with okthx, which follows up
    // itself. A task they are working on themselves sits here too — nothing
    // on the list is asking for it, and the holder line says who has it.
    has: (t) => t.status === "open" && t.assignee === "user" && (!!t.waitingOn || !!t.heldAtMs),
  },
  { key: "suggested", label: "Suggested", has: (t) => t.status === "suggested" },
  { key: "later", label: "Snoozed", has: (t) => t.status === "later" },
  // A dismissed suggestion is kept as the record of the answer, not shown.
  { key: "closed", label: "Closed", has: (t) => isClosedStatus(t.status) && !t.dismissed },
];

export type Section = {
  key: string;
  label: string;
  tasks: UserTask[];
  /** The label a section is for, so its header can open the label's page. */
  labelId?: string;
};

/**
 * The order the groups are read in, the same in every mode: a suggestion
 * first, then what the reader owes, then what is being waited on, then what
 * has been put down.
 *
 * Suggestions lead, though okthx offering a task asks nothing of anyone and
 * the reader comes to a list to work: a suggestion is the only group that goes
 * stale — it is a question, and one answered a day late has usually answered
 * itself — where the work keeps. It is also the cheapest thing on the page to
 * clear, which is what makes a list feel like it is emptying rather than
 * accumulating. Below the work, it is read last or not at all.
 *
 * Keys rather than a second copy of the groups: the predicates live once, and
 * an order that named a group that no longer exists fails loudly at the sort
 * rather than quietly dropping it.
 */
const GROUP_ORDER: readonly string[] = [
  "suggested",
  "needsYou",
  "okthx",
  // Waiting before Snoozed, which is the other way round from how this read
  // for a while. Both are parked, and the argument then was that a task dated
  // for Thursday is nearer than one waiting on a person who may answer any
  // time. But nearness is not what the reader needs from them: a scheduled
  // task has a date and comes back on its own, where a waiting one has nobody
  // chasing it — okthx follows up its own tasks, not the reader's — so it is
  // the one that quietly rots. The pair that can go stale sorts above the pair
  // that cannot.
  "waiting",
  "later",
  "closed",
];

/**
 * One section per group, in the order every mode reads them; a task lands in
 * each group whose test it meets.
 */
export function byStatus(tasks: UserTask[]): Section[] {
  return GROUP_ORDER.map((key) => {
    const group = GROUPS.find((g) => g.key === key);
    if (!group) throw new Error(`No group named ${key}`);
    return { key: group.key, label: group.label, tasks: tasks.filter(group.has) };
  });
}

/**
 * The list grouped by label: a section per label, by name, then the tasks under
 * none. Closed tasks stay out, since a label answers what is left to do. A task
 * under two labels shows in both.
 */
export function byLabel(tasks: UserTask[]): Section[] {
  const live = tasks.filter((t) => !isClosedStatus(t.status));
  const names = new Map<string, string>();
  for (const task of live) {
    for (const label of task.labels ?? []) names.set(label.id, label.name);
  }
  const sections: Section[] = [...names]
    .sort(([, a], [, b]) => a.localeCompare(b))
    .map(([id, name]) => ({
      key: `label:${id}`,
      label: name,
      labelId: id,
      tasks: live.filter((t) => t.labels?.some((label) => label.id === id)),
    }));
  sections.push({
    key: "unlabeled",
    label: "No label",
    tasks: live.filter((t) => (t.labels ?? []).length === 0),
  });
  return sections;
}
